/* NAI-53 — Page d'acceptation de l'invitation enseignant. */
/* Token en query param → définit le mot de passe → redirige vers la connexion. */

import { useState, type FormEvent, type ReactNode } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useNavigate } from '../lib/routing';
import { api } from '../lib/api';
import { isStrongPassword, PASSWORD_TOO_SHORT_MESSAGE } from '../lib/password';
import PasswordChecklist from '../components/password-checklist';
import Header from '../components/header';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function InviteAccept() {
  const search = useSearch({ strict: false }) as Record<string, string>;
  const token = search.token ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <>
        <Header />
        <main className="max-w-[480px] mx-auto mt-10 px-6 pb-16">
          <Card>
            <h1 className="mt-0 text-[22px] text-foreground">Activation du compte enseignant</h1>
            <Alert className="mt-2 mb-4">
              Lien invalide. Vérifiez le lien reçu par email ou demandez un renvoi à votre établissement.
            </Alert>
          </Card>
        </main>
      </>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isStrongPassword(password)) {
      setError(PASSWORD_TOO_SHORT_MESSAGE);
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setBusy(true);
    try {
      await api.invite.accept.mutate({ token, password });
      setDone(true);
      setTimeout(() => void navigate('/sign-in'), 2000);
    } catch (e2) {
      const msg = e2 instanceof Error ? e2.message : String(e2);
      /* Messages d'erreur métier remontés par le backend (TRPCError). */
      if (msg.includes('déjà été utilisé')) {
        setError('Ce lien a déjà été utilisé. Connectez-vous ou demandez un nouveau lien à votre établissement.');
      } else if (msg.includes('expiré')) {
        setError("Ce lien est expiré. Demandez un renvoi de l'invitation à votre établissement.");
      } else if (msg.includes('invalide')) {
        setError('Lien invalide. Vérifiez le lien reçu par email ou demandez un renvoi.');
      } else {
        setError('Une erreur est survenue. Réessayez ou contactez votre établissement.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-[480px] mx-auto mt-10 px-6 pb-16">
        <Card>
          <h1 className="mt-0 text-[22px] text-foreground">Activez votre compte enseignant</h1>
          <p className="text-sm text-muted-foreground -mt-1 mb-5">
            Choisissez un mot de passe pour accéder à votre espace Naïa.
          </p>

          {done ? (
            <Alert >Compte activé ! Redirection vers la connexion…</Alert>
          ) : (
            <form onSubmit={submit} noValidate>
              <Field label="Mot de passe">
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="10 car. : maj, min, chiffre, symbole"
                    disabled={busy}
                    className="bg-card pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    title={showPw ? 'Masquer' : 'Afficher'}
                    className="absolute right-[2px] top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-transparent border-none p-0 cursor-pointer text-[18px] leading-none"
                  >
                    {showPw ? '🙈' : '👁️'}
                  </button>
                </div>
                {password.length > 0 && <PasswordChecklist password={password} />}
              </Field>

              <Field label="Confirme le mot de passe">
                <Input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Retape-le"
                  disabled={busy}
                  className="bg-card"
                />
              </Field>

              {error && <Alert className="mt-2 mb-4">{error}</Alert>}

              <Button type="submit"  disabled={busy} className="w-full mt-2 font-black text-[18px] shadow-card">
                {busy ? 'Activation…' : 'Activer mon compte'}
              </Button>
            </form>
          )}
        </Card>
      </main>
    </>
  );
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-card p-8 shadow-card">
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <label className="block mb-2 font-bold text-foreground text-sm">
        {label}
      </label>
      {children}
    </div>
  );
}
